import {
  Directive,
  ElementRef,
  Input,
  HostListener,
  Renderer2,
  Inject,
  OnInit,
  Output,
  EventEmitter,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { WindowService } from '@cigna/shared/angular/core/window-util';

/**
 * Sticky Directive
 *
 * Make an element stick to the top or the bottom of the window, within the bounds
 * of a "sticky zone."
 *
 * Optionally, specify static elements within the sticky zone
 * that should not be overlapped by the sticky element. The position of the
 * sticky element will be shifted to avoid any overlaps with static elements
 * when the window is resized or scrolled.
 */
@Directive({
  selector: '[cignaSticky]',
})
export class StickyDirective implements OnInit {
  /**
   * CSS class applied when sticky element is fixed to window
   *
   * **NOTE:** The default class has its ruleset defined in the global.scss file.
   */
  @Input() stickyClass = 'sticky';

  /**
   * CSS class applied when sticky element is positioned absolutely
   *
   * **NOTE:** The default class has its ruleset defined in the global.scss file.
   */
  @Input() stickyScrollClass = 'sticky-scroll';

  /**
   * Minimum window width that activates sticky behavior
   */
  @Input() minimumWindowWidth = 1200;

  /**
   * Orientation for the element to stick (top or bottom)
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  @Input() stickToTop = true;

  /**
   * CSS selectors for static elements, within sticky zone, that sticky element should not overlap
   */
  @Input() staticElementSelectors: string[] = [];

  /**
   * CSS selector for container that represents sticky zone
   */
  @Input() stickyZoneSelector: string;

  /**
   * CSS selector for wrapper of sticky element
   *
   * Remains statically positioned to keep the sticky element's height in the flow.
   */
  @Input() wrapperElementSelector: string;

  // if true, sets the element's max-width to equal parent container's width
  // eslint-disable-next-line @typescript-eslint/naming-convention
  @Input() useParentContainerWidth = false;

  // if true, will unstick the element and return it to the original position at end of sticky zone
  @Input() shouldUnstickAtEndScroll = false;

  // input is the selector of the fixed header that appears for mobile views. It is used to reset upper and lower boundary for sticky zone to account for the fixed header's height
  @Input() mobileNavSelector?: string;

  // notifies parent component if element is in sticky state
  @Output() stickyClassActivated = new EventEmitter<boolean>();

  /**
   * Distance from the top of the document to the top of the window, in pixels
   */
  private topOfWindow: number;

  /**
   * Distance from the top of the document to the bottom of the window, in pixels
   */
  private bottomOfWindow: number;

  /**
   * Current width of the window
   */
  private windowWidth: number;

  /**
   * Height of sticky zone container
   */
  private stickyZoneHeight: number;

  /**
   * Height of sticky element
   */
  private stickyElementHeight: number;

  /**
   * Upper boundary for the sticky zone, in pixels
   */
  private stickyUpperBoundary: number;

  /**
   * Lower boundary for the sticky zone, in pixels
   */
  private stickyLowerBoundary: number;

  constructor(
    private el: ElementRef,
    private render: Renderer2,
    private _window: WindowService,
    @Inject(DOCUMENT) private _document: Document,
  ) {
    // Get initial width of the document
    this.windowWidth = this._window.innerWidth;
  }

  ngOnInit() {
    // Check if element should be initialized in sticky state
    this.checkStickyPosition();
  }

  /**
   * Attempt to position sticky element
   *
   * @return void
   */
  checkStickyPosition() {
    // Remove sticky classes if window is below the minimum width for stickiness
    if (this.windowWidth < this.minimumWindowWidth) {
      return this.removeSticky();
    }

    // Scan the DOM for pixel values necessary to perform calculations
    this.storeBasicDOMCalculations();

    // Get container that determines the bounds of where the sticky element can appear
    const stickyZoneContainer = this.querySelectorOrParent(
      this.el,
      this.stickyZoneSelector,
    );

    // Get wrapper for sticky element
    const wrapperElement = this.querySelectorOrParent(
      this.el,
      this.wrapperElementSelector,
    );

    // Position the sticky zone container relatively
    this.render.setStyle(stickyZoneContainer, 'position', 'relative');

    // Ensure the sticky element wrapper's height is at least the height of the sticky element
    this.render.setStyle(
      wrapperElement,
      'min-height',
      `${this.stickyElementHeight}px`,
    );

    // Determine sticky behavior based on whether element should stick to top or bottom of window
    return this.stickToTop
      ? this.checkTopStickyPosition()
      : this.checkBottomStickyPosition();
  }

  /**
   * Attempt to place sticky element towards the top of the window
   *
   * @return void
   */
  checkTopStickyPosition() {
    // Absolutely position element to top of sticky zone, if top of window is above the sticky zone
    if (this.topOfWindow < this.stickyUpperBoundary) {
      return this.shiftPositionDownOnOverlap(0) || this.setAbsoluteTop(0);
    }

    // Make element sticky, if top of window is in sticky zone
    if (
      this.topOfWindow >= this.stickyUpperBoundary &&
      this.topOfWindow <= this.stickyLowerBoundary - this.stickyElementHeight
    ) {
      return this.addStickyOrAbsoluteTop();
    }

    // Absolutely position element to bottom of sticky zone, after scrolling below
    // the point where the element fits in the sticky zone
    if (
      this.topOfWindow >
      this.stickyLowerBoundary - this.stickyElementHeight
    ) {
      return (
        this.shiftPositionUpOnOverlap(
          this.stickyZoneHeight - this.stickyElementHeight,
        ) || this.setAbsoluteBottom(0)
      );
    }

    // Remove any sticky classes, if element could not be made sticky
    return this.removeSticky();
  }

  /**
   * Attempt to place sticky element towards the bottom of the window
   *
   * @return void
   */
  checkBottomStickyPosition() {
    // Make element sticky, if bottom of window is in sticky zone
    if (
      this.bottomOfWindow >= this.stickyUpperBoundary &&
      this.bottomOfWindow <= this.stickyLowerBoundary
    ) {
      return this.addStickyOrAbsoluteBottom();
    }

    // Absolutely position element to bottom of sticky zone, after scrolling below the sticky zone
    if (this.bottomOfWindow > this.stickyLowerBoundary) {
      return (
        this.shiftPositionUpOnOverlap(
          this.stickyZoneHeight - this.stickyElementHeight,
        ) || this.setAbsoluteBottom(0)
      );
    }

    // Remove any sticky classes, if element could not be made sticky
    return this.removeSticky();
  }

  /**
   * Scan the DOM and get necessary height and boundary values
   *
   * @return void
   */
  storeBasicDOMCalculations() {
    // Get element that document scrollbar is attached to
    const scrollingElement =
      this._document.scrollingElement || this._document.documentElement;

    // Do nothing if scrolling element could not be determined
    if (!scrollingElement) {
      return;
    }

    // Get distance between top of document and top of screen, in pixels
    // use top pixels in cases when modal view is the document
    this.topOfWindow =
      scrollingElement.scrollTop ||
      (this._document.scrollingElement
        ? Math.abs((scrollingElement as HTMLElement).offsetTop)
        : Math.abs(parseInt(this._document.documentElement.style.top, 10)));

    // Get distance between top of document and bottom of screen, in pixels
    this.bottomOfWindow = this.topOfWindow + this._window.innerHeight;

    // Get sticky element
    const stickyElement = this.el.nativeElement;

    // Get container that determines the bounds of where the sticky element can appear
    const stickyZoneContainer = this.querySelectorOrParent(
      this.el,
      this.stickyZoneSelector,
    );

    // Get heights of sticky element and sticky zone container
    this.stickyZoneHeight = stickyZoneContainer.offsetHeight;
    this.stickyElementHeight = stickyElement.offsetHeight;

    // Get upper and lower boundaries for the sticky zone, in pixels
    this.stickyUpperBoundary = stickyZoneContainer.offsetTop;
    this.stickyLowerBoundary = this.stickyUpperBoundary + this.stickyZoneHeight;

    // resets the upper boundary and lower boundary to account for fixed headers for mobile views
    if (this.mobileNavSelector) {
      const navHeaderElement = this._document.querySelector(
        this.mobileNavSelector,
      ) as HTMLElement;

      if (navHeaderElement) {
        this.resetBoundariesForFixedHeaders(
          stickyZoneContainer.offsetTop,
          navHeaderElement.offsetHeight,
        );
      }
    }
  }

  /**
   * Search for an ancestor element or the direct parent element
   *
   * @param element Descendant element.
   * @param selector Selector to be queried for ancestor.
   */
  private querySelectorOrParent(element: ElementRef, selector: string) {
    let ancestorQueryResult;
    let ancestorElement;

    // Use selector to search for ancestor
    if (selector) {
      ancestorQueryResult = this._document.querySelector(selector);
    }

    // Get ancestor from selector or by using the direct parent of the given element
    // eslint-disable-next-line prefer-const
    ancestorElement = ancestorQueryResult
      ? new ElementRef(ancestorQueryResult).nativeElement
      : element.nativeElement.parentElement;

    return ancestorElement;
  }

  /**
   * Position sticky element either fixed or absolutely towards the top of the window
   *
   * @return void
   */
  private addStickyOrAbsoluteTop() {
    // Position sticky element with an offset, if it overlaps with static elements in the sticky zone
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const positionShifted = this.shiftPositionDownOnOverlap(
      Math.max(0, this.topOfWindow - this.stickyUpperBoundary),
    );

    // Do not fix element to window, if it was shifted away from static elements
    if (positionShifted) {
      return;
    }

    // Fix the sticky element to the window
    return this.addStickyFixedStyling();
  }

  /**
   * Position sticky element either fixed or absolutely towards the bottom of the window
   *
   * @return void
   */
  private addStickyOrAbsoluteBottom() {
    // Position absolutely to the top of the sticky zone,
    // if the exposed sticky zone is not big enough to fit the sticky element
    if (
      this.bottomOfWindow <=
      this.stickyUpperBoundary + this.stickyElementHeight
    ) {
      return this.shiftPositionDownOnOverlap(0) || this.setAbsoluteTop(0);
    }

    // Position sticky element with an offset, if it overlaps with static elements in the sticky zone
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const positionShifted = this.shiftPositionUpOnOverlap(
      this.bottomOfWindow - this.stickyElementHeight - this.stickyUpperBoundary,
    );

    // Do not fix element to window, if it was shifted away from static elements
    if (positionShifted) {
      return;
    }

    // Fix the sticky element to the window
    return this.addStickyFixedStyling();
  }

  /**
   * Plase sticky element at a lower position, if it would overlap static elements
   *
   * @param topPosition Offset for the top of the sticky element, relative to sticky zone.
   * @return Was position shifted down?
   */
  private shiftPositionDownOnOverlap(topPosition: number) {
    const isShiftingDown = true;

    return this.shiftPositionOnOverlap(topPosition, isShiftingDown);
  }

  /**
   * Plase sticky element at a higher position, if it would overlap static elements
   *
   * @param topPosition Offset for the top of the sticky element, relative to sticky zone.
   * @return Was position shifted up?
   */
  private shiftPositionUpOnOverlap(topPosition: number) {
    const isShiftingDown = false;

    return this.shiftPositionOnOverlap(topPosition, isShiftingDown);
  }

  /**
   * Place sticky element with an offset, if it would overlap static elements
   *
   * If placing the sticky element at the specified position would overlap with
   * static elements in the sticky zone, place the sticky element at an offset
   * position with absolute positioning to avoid overlapping static elements.
   *
   * @param topPosition Offset for the top of the sticky element, relative to sticky zone.
   * @param isShiftingDown Should the sticky element be shifted downwards if it overlaps static elements?
   * @return Was position shifted?
   */
  private shiftPositionOnOverlap(topPosition: number, isShiftingDown = true) {
    let hasShiftedPosition = false;

    // Get absolute position for top and bottom of sticky element, if it were fixed
    let upperPositionFromTop = topPosition;
    let lowerPositionFromTop = upperPositionFromTop + this.stickyElementHeight;

    for (const staticElementSelector of this.staticElementSelectors) {
      // Get first element that matches selector
      const staticElementResult = this._document.querySelector(
        staticElementSelector,
      );

      // Skip to next selector, if no elements exist for the current selector
      if (!staticElementResult) {
        continue;
      }

      // Create element reference for static element
      const staticElementRef: ElementRef = new ElementRef(staticElementResult);
      const staticElement = staticElementRef.nativeElement;

      // Get height of static element
      const staticElementHeight = staticElement.offsetHeight;

      // Get upper and lower boundaries for the static element, in pixels
      const staticElementUpperBoundary = staticElement.offsetTop;
      const staticElementLowerBoundary =
        staticElementUpperBoundary + staticElementHeight;

      // If position does not overlap the static element, skip this static element
      if (
        lowerPositionFromTop <= staticElementUpperBoundary ||
        upperPositionFromTop >= staticElementLowerBoundary
      ) {
        continue;
      }

      // Shift the position down or up to avoid overlapping the static element
      if (isShiftingDown) {
        // Move the position to directly below the static element
        upperPositionFromTop = staticElementLowerBoundary;
        lowerPositionFromTop = upperPositionFromTop + this.stickyElementHeight;
      } else {
        // Move the position to directly above the static element
        lowerPositionFromTop = staticElementUpperBoundary;
        upperPositionFromTop = lowerPositionFromTop - this.stickyElementHeight;
      }

      // Mark position as shifted
      hasShiftedPosition = true;
    }

    // Return false if position has not shifted
    if (!hasShiftedPosition) {
      return false;
    }

    // Remove sticky classes if static elements would push the sticky element out of the sticky zone
    if (
      upperPositionFromTop < 0 ||
      lowerPositionFromTop > this.stickyZoneHeight
    ) {
      this.removeSticky();

      return true;
    }

    // Position the sticky element absolutely to avoid overlapping with static elements
    this.setAbsoluteTop(upperPositionFromTop);

    return true;
  }

  /**
   * Set absolute position with top offset
   *
   * @param pixels Number of pixels to set for position.
   */
  private setAbsoluteTop(pixels: number) {
    // Position will be set from the top
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const top = true;

    // Set absolute position
    return this.setAbsoluteTopOrBottom(pixels, top);
  }

  /**
   * Set absolute position with bottom offset
   *
   * @param pixels Number of pixels to set for position.
   */
  private setAbsoluteBottom(pixels: number) {
    // Position will be set from the bottom
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const top = false;

    // Set absolute position
    return this.setAbsoluteTopOrBottom(pixels, top);
  }

  /**
   * Set absolute position with either a top or bottom offset
   *
   * @param pixels Number of pixels to set for position.
   * @param top Set top position, if true. Else, set bottom position.
   */
  private setAbsoluteTopOrBottom(pixels: number, top = true) {
    // Remove any existing top/bottom positioning
    this.removePosition();

    // Style property being set
    let property = top ? 'top' : 'bottom';

    // set the element css property to absolute at the end of the scroll
    if (this.shouldUnstickAtEndScroll && property === 'bottom') {
      property = 'absolute';
      return this.addStickyAbsoluteStyling(property, pixels);
    }

    // Add absolute positioning styling
    return this.addStickyAbsoluteStyling(property, pixels);
  }

  /**
   * Set a position offset
   *
   * @param property Position-based style property ('top' or 'bottom').
   * @param pixels Number of pixels to set for position.
   */
  private setPosition(property: string, pixels: number) {
    const pixelString = pixels.toString().concat('px');

    this.render.setStyle(this.el.nativeElement, property, pixelString);
  }

  /**
   * Add sticky styling for fixed positioning
   *
   * @return void
   */
  private addStickyFixedStyling() {
    // Remove any existing top/bottom positioning
    this.removePosition();

    // Set position for fixed element
    if (this.stickToTop) {
      this.setPosition('top', 0);
    } else {
      this.setPosition('bottom', 0);
    }

    // Add fixed sticky class and remove other sticky classes
    this.render.addClass(this.el.nativeElement, this.stickyClass);
    this.render.removeClass(this.el.nativeElement, this.stickyScrollClass);

    // sets the max width of a sticky element to the container width. need to set input useParentContainerWidth to true
    const stickyZoneWidth = this.querySelectorOrParent(
      this.el,
      this.stickyZoneSelector,
    )?.offsetWidth;
    if (this.useParentContainerWidth && stickyZoneWidth) {
      this.el.nativeElement.style.maxWidth = `${stickyZoneWidth}px`;
    }
    this.stickyClassActivated.emit(true);
  }

  /**
   * Add sticky styling to position absolutely
   *
   * @param property Position-based style property ('top' or 'bottom').
   * @param pixels Number of pixels to set for position.
   * @return void
   */
  private addStickyAbsoluteStyling(property: string, pixels: number) {
    // Set position
    this.setPosition(property, pixels);

    // Add absolute sticky class and remove other sticky classes
    this.render.addClass(this.el.nativeElement, this.stickyScrollClass);
    this.render.removeClass(this.el.nativeElement, this.stickyClass);
    this.stickyClassActivated.emit(false);
  }

  /**
   * Remove all sticky classes
   *
   * @return void
   */
  private removeSticky() {
    // Remove sticky classes
    this.render.removeClass(this.el.nativeElement, this.stickyClass);
    this.render.removeClass(this.el.nativeElement, this.stickyScrollClass);

    // Remove any top or bottom positioning
    this.removePosition();
    this.removeZonePositioning();
  }

  /**
   * Remove inline styles for top and bottom
   *
   * @return void
   */
  private removePosition() {
    this.render.removeStyle(this.el.nativeElement, 'top');
    this.render.removeStyle(this.el.nativeElement, 'bottom');
  }

  private removeZonePositioning() {
    // Get container that determines the bounds of where the sticky element can appear
    const stickyZoneContainer = this.querySelectorOrParent(
      this.el,
      this.stickyZoneSelector,
    );

    // Get wrapper for sticky element
    const wrapperElement = this.querySelectorOrParent(
      this.el,
      this.wrapperElementSelector,
    );

    // Remove positioning from container
    this.render.removeStyle(stickyZoneContainer, 'position');

    // Unset the min-height from the wrapper
    this.render.removeStyle(wrapperElement, 'min-height');
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    // Check for sticky positioning if window meets the minimum width for stickiness
    this.checkStickyPosition();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    // Recalculate window width after it is resized
    this.windowWidth = event.target.innerWidth;

    // set element max width to sticky zone width
    const stickyZoneContainer = this.querySelectorOrParent(
      this.el,
      this.stickyZoneSelector,
    );
    if (this.useParentContainerWidth && stickyZoneContainer.offsetWidth) {
      this.el.nativeElement.style.maxWidth = `${stickyZoneContainer.offsetWidth}px`;
    }

    // resets the upper boundary and lower boundary to account for fixed headers for mobile views
    if (this.mobileNavSelector) {
      const navHeaderElement = this._document.querySelector(
        this.mobileNavSelector,
      ) as HTMLElement;

      if (navHeaderElement) {
        this.resetBoundariesForFixedHeaders(
          stickyZoneContainer.offsetTop,
          navHeaderElement.offsetHeight,
        );
      }
    }

    // Check for sticky positioning if window meets the minimum width for stickiness
    this.checkStickyPosition();
  }

  private resetBoundariesForFixedHeaders(
    stickyZoneOffsetTop: number,
    navHeaderHeight: number,
  ) {
    this.stickyUpperBoundary = stickyZoneOffsetTop - navHeaderHeight;
    this.stickyLowerBoundary =
      this.stickyUpperBoundary + this.stickyZoneHeight - navHeaderHeight;
  }
}
